import { createElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BookOpenIcon, UserPlusIcon } from '@heroicons/react/24/outline';

import { logError } from 'services/logging/logging';
import { useInfiniteConnections } from 'services/repositories/connections/connections';
import { useCurrentUser } from 'services/repositories/user/user';
import { cn } from 'support/helpers/cn/cn';
import { GenericError } from 'support/helpers/errors/errors';
import { isDefined } from 'support/helpers/generic/generic';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { DocumentType, PriceCatalogRoutes, SettingsRoutes } from 'support/types';
import { Button } from 'components/Form/Button';
import { Page } from 'components/Page/Page';
import { Translate } from 'components/Translate/Translate';

const pricatFilterValues = [
  {
    key: 'message_type',
    value: DocumentType.productCatalog,
  },
];

export const MissingPricatDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { data: pricatRelationshipsData, isLoading: isLoadingPricatRelationships } = useInfiniteConnections({
    variables: {
      query: {
        filterValues: pricatFilterValues,
        perPage: '4',
      },
    },
  });
  const partnersSendingPricatTo =
    pricatRelationshipsData?.pages
      .flatMap((page) => page.data)
      .map(
        (connection) =>
          // That means the other partner is the receiver, not the current partner
          connection.receiver_relationships?.find(
            (relationship) => relationship.message_type === DocumentType.productCatalog,
          )?.receiver_partner,
      )
      .filter(isDefined) || [];

  // Format partner names with proper localization for different cases
  const getTitleWithPartnersName = () => {
    if (partnersSendingPricatTo.length === 0) {
      return '';
    }
    if (partnersSendingPricatTo.length === 1) {
      return t('dashboard:missingPricat.partnerList.single', { partner: partnersSendingPricatTo[0].name });
    }
    if (partnersSendingPricatTo.length <= 3) {
      const allButLast = partnersSendingPricatTo.slice(0, -1).map((p) => p.name);
      const last = partnersSendingPricatTo[partnersSendingPricatTo.length - 1].name;
      return t('dashboard:missingPricat.partnerList.some', {
        partners: allButLast.join(', '),
        lastPartner: last,
      });
    }
    return t('dashboard:missingPricat.partnerList.infinite', {
      partners: partnersSendingPricatTo
        .slice(0, 3)
        .map((p) => p.name)
        .join(', '),
    });
  };

  const title = getTitleWithPartnersName();

  const handleSubmitPriceCatalog = () => {
    if (!partnersSendingPricatTo || partnersSendingPricatTo.length > 1) {
      return navigate(routeToPage(PriceCatalogRoutes.root));
    }

    const firstPartner = pricatRelationshipsData?.pages[0].data[0];
    const relationshipId = firstPartner?.receiver_relationships?.find(
      (relationship) => relationship.message_type === DocumentType.productCatalog,
    )?.id;
    if (!relationshipId) {
      return logError(new GenericError('Missing pricat relationship id'));
    }
    navigate(
      routeToPage(PriceCatalogRoutes.create, {
        relationshipId,
        messageType: DocumentType.productCatalog,
      }),
    );
  };

  const handleInviteTeamMembers = () => {
    navigate(routeToPage(SettingsRoutes.teamMembers));
  };

  return (
    <Page isLoading={isLoadingPricatRelationships}>
      <Page.Head title={t('dashboard:title', { name: currentUser?.name })} />
      <Page.Section>
        <div className="mt-10 flex flex-col items-center justify-center">
          <Page.Title>{title}</Page.Title>
          <div className="mt-6 w-full max-w-[80%] space-y-3">
            <StepSection
              icon={BookOpenIcon}
              title={t('dashboard:missingPricat.sendPriceCatalog.title')}
              description={t('dashboard:missingPricat.sendPriceCatalog.description')}
              cta={
                <Button analyticsId={'dashboard:submit-price-catalog'} onClick={handleSubmitPriceCatalog}>
                  {t('dashboard:missingPricat.submitPriceCatalog')}
                </Button>
              }
            />
            <StepSection
              icon={UserPlusIcon}
              title={t('dashboard:missingPricat.inviteTeam.title')}
              description={t('dashboard:missingPricat.inviteTeam.description')}
              cta={
                <Button
                  variant="secondary"
                  analyticsId={'dashboard:invite-team-members'}
                  onClick={handleInviteTeamMembers}
                >
                  {t('dashboard:missingPricat.inviteTeamMembers')}
                </Button>
              }
            />
            <StepSection
              icon={BookOpenIcon}
              title={t('dashboard:missingPricat.help.title')}
              description={<Translate i18nKey="dashboard:missingPricat.help.description" />}
              background
            />
          </div>
        </div>
      </Page.Section>
    </Page>
  );
};

const StepSection = ({
  icon,
  title,
  description,
  cta,
  background,
}: {
  icon: React.ComponentType<any>;
  title: string;
  description: string | ReactNode;
  cta?: ReactNode;
  background?: boolean;
}) => {
  return (
    <div
      className={cn('flex w-full items-center justify-between gap-3 rounded-md border-gray-200 px-4 py-6', {
        'bg-gray-50': background,
        border: !background,
      })}
    >
      <div className="flex items-center gap-3">
        <div className="inline-flex size-10 shrink-0 items-center justify-center rounded-[32px] bg-gray-100">
          {createElement(icon, {
            className: 'h-6 w-6 shrink-0 text-gray-600',
          })}
        </div>

        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <div>{cta}</div>
    </div>
  );
};
